import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Edit, CalendarOff, UserPlus, Wrench, Zap } from "lucide-react";
import { toast } from "sonner";

function MechanicForm({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [hours, setHours] = useState(initial?.daily_hours ?? 8);
  const [role, setRole] = useState(initial?.role || "regular");
  const [vacations, setVacations] = useState(initial?.vacations || []);
  const [vStart, setVStart] = useState("");
  const [vEnd, setVEnd] = useState("");

  const addVac = () => {
    if (!vStart || !vEnd) return;
    if (vStart > vEnd) { toast.error("Fecha inicio > fin"); return; }
    setVacations((vs) => [...vs, { start: vStart, end: vEnd }]);
    setVStart(""); setVEnd("");
  };
  const removeVac = (i) => setVacations((vs) => vs.filter((_, idx) => idx !== i));

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nombre requerido"); return; }
    if (hours <= 0) { toast.error("Horas debe ser > 0"); return; }
    onSave({ name: name.trim(), daily_hours: Number(hours), role, vacations });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="mname">Nombre</Label>
        <Input id="mname" value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="mech-name-input" />
      </div>

      <div className="space-y-2">
        <Label>Especialidad</Label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setRole("regular")}
            data-testid="mech-role-regular"
            className={`p-3 rounded-md border-2 flex flex-col items-center gap-1 transition-colors ${
              role === "regular" ? "border-blue-700 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
            }`}
          >
            <Wrench className={`w-5 h-5 ${role === "regular" ? "text-blue-700" : "text-slate-500"}`} />
            <span className={`font-medium text-sm ${role === "regular" ? "text-blue-700" : "text-slate-700"}`}>Mecánica General</span>
            <span className="text-xs text-slate-500">Cuenta para la capacidad</span>
          </button>
          <button
            type="button"
            onClick={() => setRole("quick")}
            data-testid="mech-role-quick"
            className={`p-3 rounded-md border-2 flex flex-col items-center gap-1 transition-colors ${
              role === "quick" ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:bg-slate-50"
            }`}
          >
            <Zap className={`w-5 h-5 ${role === "quick" ? "text-amber-600" : "text-slate-500"}`} />
            <span className={`font-medium text-sm ${role === "quick" ? "text-amber-700" : "text-slate-700"}`}>Mecánica Rápida</span>
            <span className="text-xs text-slate-500">Aparte de la capacidad</span>
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="mhours">Horas diarias</Label>
        <Input id="mhours" type="number" step="0.5" min="0.5" value={hours} onChange={(e) => setHours(e.target.value)} className="h-11" data-testid="mech-hours-input" />
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-2"><CalendarOff className="w-4 h-4" /> Vacaciones</Label>
        <div className="flex flex-wrap gap-2 items-end">
          <Input type="date" value={vStart} onChange={(e) => setVStart(e.target.value)} className="h-11" data-testid="vac-start-input" />
          <Input type="date" value={vEnd} onChange={(e) => setVEnd(e.target.value)} className="h-11" data-testid="vac-end-input" />
          <Button type="button" variant="outline" onClick={addVac} className="h-11" data-testid="vac-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Añadir
          </Button>
        </div>
        {vacations.length > 0 && (
          <ul className="space-y-1 mt-2">
            {vacations.map((v, i) => (
              <li key={i} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm">
                <span>{v.start} → {v.end}</span>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => removeVac(i)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} className="h-11">Cancelar</Button>
        <Button type="submit" className="h-11 bg-blue-700 hover:bg-blue-800" data-testid="mech-save-btn">Guardar</Button>
      </DialogFooter>
    </form>
  );
}

function MechanicCard({ m, onEdit, onDelete, onAddVacation }) {
  const isQuick = m.role === "quick";
  return (
    <Card data-testid={`mechanic-${m.id}`} className={isQuick ? "border-amber-200" : ""}>
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isQuick ? <Zap className="w-5 h-5 text-amber-500" /> : <Wrench className="w-5 h-5 text-blue-700" />}
            <span>{m.name}</span>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(m)} data-testid={`edit-mech-${m.id}`}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => onDelete(m.id)} data-testid={`delete-mech-${m.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardTitle>
        <div className={`inline-block w-fit text-xs px-2 py-0.5 rounded font-medium ${
          isQuick ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"
        }`}>
          {isQuick ? "Mecánica Rápida" : "Mecánica General"}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-3xl font-display font-bold text-slate-900">{m.daily_hours}h<span className="text-base text-slate-500 ml-2">/ día</span></div>
          <Button
            size="sm"
            onClick={() => onAddVacation(m)}
            className="h-10 bg-red-600 hover:bg-red-700 text-white"
            data-testid={`add-vacation-${m.id}`}
          >
            <CalendarOff className="w-4 h-4 mr-1" /> Añadir vacaciones
          </Button>
        </div>
        {m.vacations?.length > 0 ? (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
              <CalendarOff className="w-3 h-3" /> Vacaciones
            </div>
            <ul className="space-y-1 text-sm text-slate-700">
              {m.vacations.map((v, i) => (
                <li key={i}>{v.start} → {v.end}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mt-3 text-xs text-slate-400">Sin vacaciones programadas</div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Mechanics() {
  const [list, setList] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/mechanics");
    setList(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (data) => {
    try {
      await api.post("/mechanics", data);
      toast.success("Mecánico añadido");
      setOpenCreate(false);
      load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo añadir"));
    }
  };
  const update = async (data) => {
    try {
      await api.put(`/mechanics/${editTarget.id}`, data);
      toast.success("Mecánico actualizado");
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo actualizar"));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este mecánico?")) return;
    try {
      await api.delete(`/mechanics/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo eliminar"));
    }
  };

  const regular = list.filter((m) => m.role !== "quick");
  const quick = list.filter((m) => m.role === "quick");

  return (
    <div className="space-y-6" data-testid="mechanics-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Mecánicos</h1>
          <p className="text-slate-500 mt-1">Especialidad, horas diarias y vacaciones.</p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button className="h-12 bg-blue-700 hover:bg-blue-800" data-testid="add-mechanic-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Añadir mecánico
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">Nuevo mecánico</DialogTitle></DialogHeader>
            <MechanicForm onSave={create} onCancel={() => setOpenCreate(false)} />
          </DialogContent>
        </Dialog>
      </header>

      <section>
        <h2 className="font-display text-xl font-semibold text-slate-900 flex items-center gap-2 mb-3">
          <Wrench className="w-5 h-5 text-blue-700" /> Mecánica General
          <span className="text-sm font-normal text-slate-500">· {regular.length} · cuenta para la capacidad</span>
        </h2>
        {regular.length === 0 ? (
          <Card><CardContent className="py-6 text-center text-slate-400 italic" data-testid="regular-empty">Sin mecánicos generales. Sin ellos no se programan citas.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {regular.map((m) => <MechanicCard key={m.id} m={m} onEdit={setEditTarget} onDelete={remove} onAddVacation={setEditTarget} />)}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display text-xl font-semibold text-slate-900 flex items-center gap-2 mb-3">
          <Zap className="w-5 h-5 text-amber-500" /> Mecánica Rápida
          <span className="text-sm font-normal text-slate-500">· {quick.length} · aparte de la capacidad</span>
        </h2>
        {quick.length === 0 ? (
          <Card><CardContent className="py-6 text-center text-slate-400 italic" data-testid="quick-empty-mech">Sin mecánicos de mecánica rápida.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quick.map((m) => <MechanicCard key={m.id} m={m} onEdit={setEditTarget} onDelete={remove} onAddVacation={setEditTarget} />)}
          </div>
        )}
      </section>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Editar mecánico</DialogTitle></DialogHeader>
          {editTarget && <MechanicForm initial={editTarget} onSave={update} onCancel={() => setEditTarget(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
